(function() {
  const initializedCarousels = new WeakMap();

  /**
   * Get responsive slidesToScroll based on viewport width
   * Desktop: 3.5 slides (3 whole + 1/2)
   * Mobile: 1 slide
   * @returns {number}
   */
  function getSlidesToScroll() {
    if (window.matchMedia('(min-width: 990px)').matches) {
      return 1; // Desktop: scroll 1 slide at a time, but show 3.5 slides
    }
    return 1; // Mobile: scroll 1 slide at a time, show 1 slide
  }

  /**
   * Get responsive align based on viewport width
   * @returns {string}
   */
  function getAlign() {
    return window.matchMedia('(min-width: 990px)').matches ? 'start' : 'start';
  }

  /**
   * Setup dots for carousel
   * @param {HTMLElement} carouselElement
   * @param {any} embla
   * @param {HTMLElement} dotsContainer
   * @param {string} carouselId
   */
  function setupDots(carouselElement, embla, dotsContainer, carouselId) {
    if (!dotsContainer) return;

    try {
      const slides = carouselElement.querySelectorAll('.nf-reviews-slider__slide');
      
      if (!slides.length) {
        dotsContainer.innerHTML = '';
        return;
      }

      // Get the actual snap points from Embla
      let totalPages = 0;
      try {
        if (typeof embla.scrollSnapList === 'function') {
          const snapList = embla.scrollSnapList();
          totalPages = snapList?.length || 0;
        } else {
          throw new Error('scrollSnapList not available');
        }
      } catch (e) {
        // Fallback: calculate manually if scrollSnapList isn't available
        const carouselData = initializedCarousels.get(carouselElement);
        const visibleSlidesPerPage = carouselData ? carouselData.slidesToScroll : getSlidesToScroll();
        totalPages = Math.max(1, Math.ceil(slides.length / visibleSlidesPerPage));
      }

      // Only show dots if there are multiple pages
      if (totalPages <= 1) {
        dotsContainer.innerHTML = '';
        return;
      }

      // Clear existing dots
      dotsContainer.innerHTML = '';

      // Create dots based on actual snap points
      for (let i = 0; i < totalPages; i++) {
        const dot = document.createElement('button');
        dot.className = 'nf-reviews-slider__dot';
        dot.setAttribute('type', 'button');
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        
        const snapIndex = i;
        dot.addEventListener('click', () => {
          embla.scrollTo(snapIndex);
        });
        
        dotsContainer.appendChild(dot);
      }
    } catch (error) {
      console.warn('Reviews Carousel: Error setting up dots', error);
      if (dotsContainer) {
        dotsContainer.innerHTML = '';
      }
    }
  }

  /**
   * Update active dot state
   * @param {HTMLElement} dotsContainer
   * @param {any} embla
   */
  function updateDots(dotsContainer, embla) {
    if (!dotsContainer) return;

    const dots = dotsContainer.querySelectorAll('.nf-reviews-slider__dot');
    if (dots.length === 0) return;

    try {
      const selectedSnapIndex = embla.selectedScrollSnap();
      const activePageIndex = Math.max(0, Math.min(selectedSnapIndex, dots.length - 1));
      
      dots.forEach((dot, index) => {
        if (index === activePageIndex) {
          dot.classList.add('nf-reviews-slider__dot--active');
        } else {
          dot.classList.remove('nf-reviews-slider__dot--active');
        }
      });
    } catch (error) {
      console.warn('Reviews Carousel: Error updating dots', error);
    }
  }

  /**
   * Initialize carousel with Embla
   * @param {HTMLElement} carouselElement
   */
  function initCarousel(carouselElement) {
    if (!carouselElement) return;
    
    // Skip if already initialized
    if (initializedCarousels.has(carouselElement)) return;

    if (typeof EmblaCarousel === 'undefined') {
      setTimeout(() => initCarousel(carouselElement), 100);
      return;
    }

    const currentSlidesToScroll = getSlidesToScroll();
    const currentAlign = getAlign();
    
    const embla = EmblaCarousel(carouselElement, {
      loop: false,
      align: currentAlign,
      containScroll: 'trimSnaps',
      slidesToScroll: currentSlidesToScroll,
      dragFree: false
    });

    initializedCarousels.set(carouselElement, { embla, slidesToScroll: currentSlidesToScroll, align: currentAlign });

    const sliderContainer = carouselElement.closest('.nf-reviews-slider');
    if (!sliderContainer) {
      console.warn('Reviews Carousel: Could not find slider container');
      return;
    }

    const dotsContainer = sliderContainer.querySelector('.nf-reviews-slider__dots');
    const carouselId = dotsContainer?.getAttribute('data-carousel-id');
    
    if (!carouselId) {
      console.warn('Reviews Carousel: Could not find carousel ID');
      return;
    }

    const prevButton = sliderContainer.querySelector(`.nf-reviews-slider__button--prev[data-carousel-id="${carouselId}"]`);
    const nextButton = sliderContainer.querySelector(`.nf-reviews-slider__button--next[data-carousel-id="${carouselId}"]`);

    const updateButtons = () => {
      if (prevButton && prevButton instanceof HTMLElement && 'disabled' in prevButton) {
        (/** @type {HTMLButtonElement} */ (prevButton)).disabled = !embla.canScrollPrev();
      }
      if (nextButton && nextButton instanceof HTMLElement && 'disabled' in nextButton) {
        (/** @type {HTMLButtonElement} */ (nextButton)).disabled = !embla.canScrollNext();
      }
    };

    if (prevButton) {
      prevButton.addEventListener('click', () => embla.scrollPrev());
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => embla.scrollNext());
    }

    embla.on('select', () => {
      updateButtons();
      if (dotsContainer) {
        updateDots(dotsContainer, embla);
      }
    });

    embla.on('init', () => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (dotsContainer) {
            setupDots(carouselElement, embla, dotsContainer, carouselId);
            updateButtons();
            embla.scrollTo(0);
            updateDots(dotsContainer, embla);
          }
        }, 100);
      });
    });

    embla.on('reInit', () => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (dotsContainer) {
            setupDots(carouselElement, embla, dotsContainer, carouselId);
            updateButtons();
            embla.scrollTo(0);
            updateDots(dotsContainer, embla);
          }
        }, 100);
      });
    });

    embla.on('settle', () => {
      if (dotsContainer) {
        updateDots(dotsContainer, embla);
      }
    });

    // Initial setup
    updateButtons();

    // Handle window resize to adjust slidesToScroll and recalculate dots
    let resizeTimer;
    const handleResize = () => {
      if (resizeTimer !== undefined) {
        clearTimeout(resizeTimer);
      }
      resizeTimer = setTimeout(() => {
        const carouselData = initializedCarousels.get(carouselElement);
        if (!carouselData) return;
        
        const newSlidesToScroll = getSlidesToScroll();
        const newAlign = getAlign();
        if (carouselData.slidesToScroll !== newSlidesToScroll || carouselData.align !== newAlign) {
          carouselData.embla.destroy();
          initializedCarousels.delete(carouselElement);
          initCarousel(carouselElement);
        } else {
          if (dotsContainer) {
            setupDots(carouselElement, embla, dotsContainer, carouselId);
            updateDots(dotsContainer, embla);
          }
        }
      }, 250);
    };
    window.addEventListener('resize', handleResize);
  }

  function initAllCarousels() {
    const carousels = document.querySelectorAll('.nf-reviews-slider__viewport:not([data-initialized])');
    carousels.forEach((carousel) => {
      if (carousel instanceof HTMLElement) {
        carousel.setAttribute('data-initialized', 'true');
        initCarousel(carousel);
      }
    });
  }

  function handleThemeEditorUpdates() {
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:load', () => {
        setTimeout(() => {
          initAllCarousels();
        }, 100);
      });
      document.addEventListener('shopify:section:reorder', () => {
        setTimeout(() => {
          initAllCarousels();
        }, 100);
      });
      document.addEventListener('shopify:block:select', () => {
        setTimeout(() => {
          initAllCarousels();
        }, 100);
      });
    }
  }

  // Initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAllCarousels();
      handleThemeEditorUpdates();
    });
  } else {
    initAllCarousels();
    handleThemeEditorUpdates();
  }

  // Use MutationObserver for theme editor updates
  if (window.Shopify?.designMode) {
    const observer = new MutationObserver(() => {
      initAllCarousels();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();

